import React, { useCallback } from "react";
import { useField, useFormikContext } from "formik";
import { RichTextEditor, clearHTMLTags } from "./RichTextEditor";
import type { RichTextEditorProps } from "./RichTextEditor";

interface FormikRichTextEditorProps extends Omit<RichTextEditorProps, "value" | "onChange"> {
	name: string;
}

export const FormikRichTextEditor: React.FC<FormikRichTextEditorProps> = ({
	name,
	maxLength,
	...props
}) => {
	const validate = useCallback(
		(value: string) => {
			if (maxLength && maxLength > 0) {
				const plainText = clearHTMLTags(value || "");
				if (plainText.length > maxLength) {
					return `Content exceeds maximum length by ${plainText.length - maxLength} character${plainText.length - maxLength !== 1 ? "s" : ""}`;
				}
			}
			return undefined;
		},
		[maxLength]
	);

	const [field, meta] = useField<string>({ name, validate });
	const { setFieldValue, setFieldTouched } = useFormikContext();

	const hasError = !!(meta.error && meta.touched);

	const handleChange = useCallback(
		(html: string) => {
			setFieldValue(name, html);
			setFieldTouched(name, true);
		},
		[name, setFieldValue, setFieldTouched]
	);

	return (
		<RichTextEditor
			{...props}
			maxLength={maxLength}
			value={field.value || ""}
			onChange={handleChange}
			hasError={hasError}
			errorText={meta.touched ? meta.error : undefined}
		/>
	);
};
